import type {
  RouteLocationNormalizedLoadedGeneric,
  RouteLocationRaw,
} from 'vue-router'

type Step = {
  completed: boolean
  title: string
  to: RouteLocationRaw
}

export default function (storeId: string) {
  const storeKey = `steps-${storeId}`

  return defineStore(
    storeId,
    () => {
      const currentCookie = useCookie(storeKey)
      const steps = ref<Step[]>([])
      const currentStepIndex = ref(0)

      const localePath = useLocalePath()

      const currentStep = computed(() => steps.value[currentStepIndex.value])

      const initSteps = async (s: Omit<Step, 'completed'>[]) => {
        if (
          currentCookie.value
          && steps.value.length
          && steps.value.length !== s.length
        ) {
          currentCookie.value = null
          navigateTo('/')
          return
        }

        if (steps.value.length) {
          steps.value = steps.value.map((step, index) => ({
            completed: step.completed,
            title: s[index].title,
            to: s[index].to,
          }))
          return
        }

        steps.value = s.map(step => ({ ...step, completed: false }))
      }

      const setCurrentStep = (
        step: Step,
        redirect: 'hard' | 'soft' | false = false,
      ) => {
        currentStepIndex.value = steps.value.indexOf(step)

        if (redirect === false) {
          return
        }

        return navigateTo(localePath(step.to), {
          external: redirect === 'hard',
        })
      }

      const navigateStep = async (direction: 'back' | 'forward') => {
        const stepDiff = direction === 'back' ? -1 : 1
        const newStepIndex = currentStepIndex.value + stepDiff

        if (newStepIndex < 0) {
          return navigateTo('/')
        }

        if (newStepIndex >= steps.value.length) {
          return
        }

        return setCurrentStep(steps.value[newStepIndex], 'soft')
      }

      const stepsGuardMiddleware = async (
        to: RouteLocationNormalizedLoadedGeneric,
      ) => {
        const pathStep = steps.value.find(
          step => localePath(step.to) === to.path,
        )

        if (!pathStep) {
          return
        }

        setCurrentStep(pathStep)

        const pathStepIndex = steps.value.indexOf(pathStep)

        if (!pathStepIndex || pathStep.completed) {
          return
        }

        const lastCompletedStep = steps.value
          .toReversed()
          .find(step => step.completed)

        if (!lastCompletedStep) {
          return setCurrentStep(steps.value[0], 'hard')
        }

        const lastCompletedStepIndex = steps.value.indexOf(lastCompletedStep)

        if (lastCompletedStepIndex === pathStepIndex - 1) {
          return
        }

        const redirectStep
          = steps.value?.[lastCompletedStepIndex + 1] ?? steps.value[0]

        return setCurrentStep(redirectStep, 'hard')
      }

      return {
        currentStep,
        currentStepIndex,
        initSteps,
        isFirstStep: computed(() => currentStepIndex.value === 0),
        isLastStep: computed(
          () => currentStepIndex.value === steps.value.length - 1,
        ),
        navigateStep,
        nextStep: computed(() => steps.value?.[currentStepIndex.value + 1]),
        progress: computed(
          () => (currentStepIndex.value / (steps.value.length - 1)) * 100,
        ),
        steps, // If not exposed, the state is not persisted. https://github.com/prazdevs/pinia-plugin-persistedstate/issues/244
        stepsGuardMiddleware,
        stepsRemaining: computed(
          () => steps.value.length - currentStepIndex.value - 1,
        ),
      }
    },
    {
      persist: {
        key: storeKey,
      },
    },
  )()
}
